import { fromCodeData } from './CodeData.js';
import {
  asBoolean,
  asInstant,
  mapDtoList,
  optionalEnum,
  optionalNumber,
  optionalString,
  requireObject,
} from './parse.js';

/**
 * Mirrors com.medprompt.content.measurement.MeasurementSignificance
 */
export const MeasurementSignificance = Object.freeze({
  CORE: 'CORE',
  EXTENDED: 'EXTENDED',
});

/**
 * Mirrors com.medprompt.content.measurement.MeasurementComparator
 */
export const MeasurementComparator = Object.freeze({
  EQ: 'EQ',
  GT: 'GT',
  LT: 'LT',
});

/**
 * Mirrors com.medprompt.content.measurement.MeasurementValueType
 */
export const MeasurementValueType = Object.freeze({
  NUMERIC: 'NUMERIC',
  NONE: 'NONE',
});

/**
 * Mirrors com.medprompt.content.measurement.Measurement.
 * `type` is a MeasurementType name; that enum is open-ended on the server, so it is not listed here.
 *
 * @typedef {object} MeasurementData
 * @property {string|null} measurementId
 * @property {string|null} type
 * @property {string|null} significance
 * @property {string|null} comparator
 * @property {string|null} valueType
 * @property {number|null} value
 * @property {string|null} unit
 * @property {import('./CodeData.js').CodeData|null} code
 * @property {number|string|null} date
 * @property {boolean} isSelected
 */

/**
 * @param {object} raw
 * @returns {MeasurementData}
 */
export function fromMeasurementData(raw) {
  const source = requireObject(raw, 'Measurement');
  return {
    measurementId: optionalString(source.measurementId),
    type: optionalString(source.type),
    significance: optionalEnum(
      source.significance, MeasurementSignificance, 'Measurement', 'significance'),
    comparator: optionalEnum(source.comparator, MeasurementComparator, 'Measurement', 'comparator'),
    valueType: optionalEnum(source.valueType, MeasurementValueType, 'Measurement', 'valueType'),
    value: optionalNumber(source.value),
    unit: optionalString(source.unit),
    code: source.code == null ? null : fromCodeData(source.code),
    date: asInstant(source.date),
    isSelected: asBoolean(source.isSelected),
  };
}

/**
 * @param {unknown} raw
 * @returns {MeasurementData[]}
 */
export function fromMeasurementDataList(raw) {
  return mapDtoList(raw, 'Measurement', fromMeasurementData);
}
